import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";

const STORAGE_KEY = "island_last_order";

/**
 * Dedicated Order Submitted confirmation page.
 * Order data flows in via `location.state.order` (from CartSidebar navigation)
 * and is also persisted to sessionStorage so a page refresh doesn't lose it.
 */
export default function OrderConfirmation() {
  const { state } = useLocation();
  const [order, setOrder] = useState(state?.order || null);

  useEffect(() => {
    if (state?.order) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.order));
    } else {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        try { setOrder(JSON.parse(raw)); } catch { /* ignore */ }
      }
    }
    window.scrollTo(0, 0);
  }, [state]);

  // If nobody has ever placed an order in this session, send them to products
  if (!order) return <Navigate to="/products" replace />;

  const ref = order.order_id.slice(0, 8).toUpperCase();

  return (
    <div data-testid="order-confirmation-page" className="fade-up">
      <section className="max-w-[820px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 border border-brand-accent rounded-full mb-8">
          <CheckCircle2 strokeWidth={1.2} className="w-7 h-7 text-brand-accent" />
        </div>
        <p className="overline hidden">Order Submitted</p>
        <h1
          data-testid="order-confirmation-title"
          className="font-serif font-light text-3xl sm:text-4xl md:text-5xl text-brand-dark mt-6 leading-[1.1]"
        >
          Thank you for<br />your <span className="italic">order</span>.
        </h1>
        <p className="mt-8 text-brand-neutral text-lg leading-relaxed max-w-xl mx-auto">
          Your order has been successfully submitted to The Island Studio.
        </p>
      </section>

      <section className="max-w-[820px] mx-auto px-6 md:px-12 pb-16">
        <div className="border border-brand-border p-8 md:p-12 bg-white">
          <p className="overline text-[10px]">Your Order Reference</p>
          <p data-testid="order-confirmation-ref" className="font-serif text-3xl md:text-4xl text-brand-dark tracking-widest mt-2">
            #{ref}
          </p>

          <div className="mt-8 space-y-6 text-brand-neutral leading-relaxed">
            <p>
              A copy of your order has been sent to our team for review. We will review your
              order to confirm product availability, delivery or collection details, and any
              applicable shipping costs.
            </p>

            <div>
              <p className="text-brand-dark">
                Once your order has been reviewed, you will receive an email containing:
              </p>
              <ul className="mt-4 space-y-2.5">
                {[
                  "A detailed invoice",
                  "Bank transfer payment instructions",
                  "Your order reference number",
                  "Delivery or collection details",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-accent shrink-0"
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-6 mt-6 border-t border-brand-border">
              <p className="overline text-[10px] mb-2">Please note</p>
              <p className="italic text-brand-dark">
                Your order is considered a <span className="text-brand-accent not-italic">purchase request</span> and
                is not confirmed until payment has been received.
              </p>
            </div>
          </div>

          {/* Delivery / collection details (if applicable) */}
          {order.delivery_method && (
            <div className="mt-10 pt-8 border-t border-brand-border">
              <p className="overline text-[10px] mb-3">
                {order.delivery_method === "collection" ? "Collection Details" : "Delivery Details"}
              </p>
              {order.delivery_method === "collection" && (
                <p data-testid="confirm-delivery-details" className="text-brand-neutral leading-relaxed">
                  <span className="text-brand-dark">Collection · Love Beach</span><br />
                  We'll email you to arrange a collection time once your order has been reviewed and confirmed.
                </p>
              )}
              {order.delivery_method === "local_delivery" && (
                <p data-testid="confirm-delivery-details" className="text-brand-neutral leading-relaxed">
                  <span className="text-brand-dark">Nassau Delivery</span><br />
                  <span className="whitespace-pre-wrap">{order.delivery_address}</span>
                </p>
              )}
              {order.delivery_method === "family_island" && (
                <p data-testid="confirm-delivery-details" className="text-brand-neutral leading-relaxed">
                  <span className="text-brand-dark">Family Island Delivery — {order.family_island_name}</span><br />
                  <span className="whitespace-pre-wrap">{order.delivery_address}</span>
                  <br /><br />
                  <em className="not-italic text-xs uppercase tracking-widest text-brand-accent">
                    Delivery cost quoted separately
                  </em>
                </p>
              )}
            </div>
          )}

          {/* Order totals summary */}
          <div className="mt-10 pt-8 border-t border-brand-border">
            <p className="overline text-[10px] mb-4">Order Summary</p>
            <div className="text-sm space-y-1.5 text-brand-neutral">
              <div className="flex justify-between">
                <span>Products Subtotal</span>
                <span data-testid="confirm-products-subtotal">${order.products_subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery</span>
                <span data-testid="confirm-delivery-fee">
                  ${order.delivery_fee.toFixed(2)}
                  {order.delivery_tbc && (
                    <em className="not-italic text-brand-accent ml-2 text-xs uppercase tracking-widest">
                      TBC
                    </em>
                  )}
                </span>
              </div>
              <div className="flex justify-between pt-2 mt-2 border-t border-brand-border text-brand-dark">
                <span>Subtotal (before VAT)</span>
                <span data-testid="confirm-pre-vat">${order.pre_vat_subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>VAT (10%)</span>
                <span data-testid="confirm-vat">${order.vat.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-3 mt-3 border-t border-brand-border">
                <span className="font-serif text-lg text-brand-dark">
                  {order.delivery_tbc ? "Total Due (excl. delivery)" : "Total Due"}
                </span>
                <span
                  data-testid="confirm-total"
                  className="font-serif text-lg text-brand-accent"
                >
                  ${order.total.toFixed(2)}
                </span>
              </div>
            </div>
            {order.delivery_tbc && (
              <p className="mt-4 text-xs text-brand-neutral leading-relaxed">
                Family Island delivery costs will be quoted separately based on destination and
                shipping method and added to your final invoice.
              </p>
            )}
          </div>
        </div>

        <p className="mt-10 text-center font-serif italic text-2xl md:text-3xl text-brand-accent">
          Thank you for supporting a Bahamian-made business.
        </p>

        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/products" data-testid="confirm-continue-shopping" className="btn-primary">
            Continue Browsing <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
          </Link>
          <Link to="/" data-testid="confirm-go-home" className="btn-outline">
            Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
}
